import { useEffect, useMemo, useState } from "react";
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatLocalYmd,
  monthKeyOf,
  monthLabel,
  monthWeeks,
  parseLocalYmd,
  shiftMonthKey,
  weekStartFor,
  weekdayNames,
  formatDayMonthYear,
} from "@heraion/shared";

import { Icon } from "../Icon/Icon";
import type { UiTheme } from "../theme";
import { hiddenFromScreenReader } from "./hiddenFromScreenReader";
import { IconButton } from "./IconButton";
import { useLocale } from "./useLocale";
import { useDateLocale } from "./DateFormatContext";

// #368: a month of days to pick one from, or to read what is planned on each.
// Marks, never colour alone: a planned day has a dot (and its count from 2),
// a day with a workout done a check (#374), today is bold and underlined in
// the neutral ink, the picked day has a brand ring on its tint. The week
// starts as the language has it (weekStartFor).
// Days are "YYYY-MM-DD" in local time, never parsed as UTC (monthCalendar.ts).

/** A day's row step, ≥44 tall. */
const CELL_STEP = 46;
/** The drawn day inside it, picked ring included. */
const DAY_BOX_WIDTH = 40;
const DAY_BOX_HEIGHT = 42;
/** The marks' line under the number: the check, the dot and the count. The
 *  sizes at 1x: they grow with the text, the check up to CHECK_MAX. */
const MARKS_HEIGHT = 14;
const DOT = 6;
const CHECK = 12;
const CHECK_MAX = 18;
type MarkSizes = { check: number; dot: number; height: number };
const markSizes = (fontScale: number): MarkSizes => {
  const k = Math.min(Math.max(fontScale, 1), CHECK_MAX / CHECK);
  return { check: Math.round(CHECK * k), dot: Math.round(DOT * k), height: Math.round(MARKS_HEIGHT * k) };
};
const CELL_SLOP = { left: 2, right: 2 };
export type MonthCalendarProps = {
  theme: UiTheme;
  /** The picked day, "" for none. */
  value: string;
  onChange: (ymd: string) => void;
  /** How many things are planned on a day. */
  markers?: Readonly<Record<string, number>>;
  /** #374: how many workouts were done on a day. Absent: no check, and no
   *  legend line for it. */
  done?: Readonly<Record<string, number>>;
  /** #374: the days with a plan not done, said in the day's label. */
  missed?: ReadonlySet<string>;
  /** Days before it cannot be picked. */
  minDate?: string;
  /** Today; the device's own day when absent. */
  today?: string;
};

export function MonthCalendar({ theme, value, onChange, markers, done, missed, minDate, today }: MonthCalendarProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dateLocale = useDateLocale();
  const { fontScale } = useWindowDimensions();
  const sizes = useMemo(() => markSizes(fontScale), [fontScale]);
  const styles = useMemo(() => makeStyles(theme, sizes), [theme, sizes]);
  const todayYmd = today ?? formatLocalYmd(new Date());
  const [shown, setShown] = useState(() => monthKeyOf(value || todayYmd));
  // A day picked from outside (Clear, a resumed draft) brings its month.
  useEffect(() => {
    if (value) setShown(monthKeyOf(value));
  }, [value]);
  const weekStart = weekStartFor(locale);
  const shortDays = useMemo(() => weekdayNames(locale, weekStart, "min"), [locale, weekStart]);
  const longDays = useMemo(() => weekdayNames(locale, 0, "long"), [locale]);
  const weeks = useMemo(() => monthWeeks(shown, weekStart), [shown, weekStart]);
  const atMin = minDate !== undefined && shown <= monthKeyOf(minDate);

  const cell = (ymd: string | null, i: number) => {
    if (ymd === null) return <View key={`pad-${i}`} style={styles.cell} />;
    const date = parseLocalYmd(ymd);
    const picked = ymd === value;
    const isToday = ymd === todayYmd;
    const past = minDate !== undefined && ymd < minDate;
    const count = markers?.[ymd] ?? 0;
    const doneCount = done?.[ymd] ?? 0;
    const label = [
      date ? `${longDays[date.getDay()]}, ${formatDayMonthYear(date, dateLocale)}` : ymd,
      ...(isToday ? [t("calendar.today")] : []),
      ...(count > 0 ? [t("calendar.planned", { count })] : []),
      ...(missed?.has(ymd) ? [t("calendar.missed")] : []),
      ...(doneCount > 0 ? [t("calendar.done", { count: doneCount })] : []),
    ].join(", ");
    return (
      <Pressable
        key={ymd}
        // A toggle the web reads as pressed (`aria-selected` is not valid on
        // a button, and RNW drops accessibilityState); native reads selected.
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ selected: picked, disabled: past }}
        {...{ "aria-pressed": picked }}
        disabled={past}
        onPress={() => onChange(ymd)}
        // A cell can be narrower than 44 on a 320 phone: the slop makes it up
        // natively. Not on the web (RNW has no hitSlop), and a min width of 44
        // would push Saturday out of a grid under 308 wide (the plan sheet).
        hitSlop={CELL_SLOP}
        style={styles.cell}
      >
        {({ pressed }) => (
          <View style={[styles.box, picked && styles.boxPicked, pressed && styles.boxPressed]}>
            <Text style={[styles.num, isToday && styles.today, past && styles.past]}>
              {Number(ymd.slice(8))}
            </Text>
            <View style={styles.marks}>
              {doneCount > 0 ? <Icon name="check" size={sizes.check} color={theme.palette.ink2} /> : null}
              {count > 0 ? <View style={styles.dot} /> : null}
              {count > 1 ? <Text style={styles.count}>{count}</Text> : null}
            </View>
          </View>
        )}
      </Pressable>
    );
  };

  return (
    <View style={styles.wrap}>
      <View style={styles.head}>
        <IconButton
          theme={theme}
          name="chevron-left"
          accessibilityLabel={t("calendar.prevMonth")}
          onPress={() => setShown((k) => shiftMonthKey(k, -1))}
          disabled={atMin}
        />
        <Text style={styles.month} accessibilityRole="header" accessibilityLiveRegion="polite">
          {monthLabel(shown, locale)}
        </Text>
        <IconButton
          theme={theme}
          name="chevron-right"
          accessibilityLabel={t("calendar.nextMonth")}
          onPress={() => setShown((k) => shiftMonthKey(k, 1))}
        />
      </View>
      <View style={[styles.week, styles.dowRow]} {...hiddenFromScreenReader()}>
        {shortDays.map((d) => (
          <Text key={d} style={styles.dow} numberOfLines={1}>
            {d}
          </Text>
        ))}
      </View>
      {weeks.map((w, wi) => (
        <View key={w.find((d) => d !== null) ?? wi} style={styles.week}>
          {w.map((d, i) => cell(d, wi * 7 + i))}
        </View>
      ))}
      <View style={styles.legend}>
        <View style={styles.legendItem}>
          <View {...hiddenFromScreenReader()} style={styles.dot} />
          <Text style={styles.legendText}>{t("calendar.legendPlanned")}</Text>
        </View>
        {done ? (
          <View style={styles.legendItem}>
            <View {...hiddenFromScreenReader()}>
              <Icon name="check" size={sizes.check} color={theme.palette.ink2} />
            </View>
            <Text style={styles.legendText}>{t("calendar.legendDone")}</Text>
          </View>
        ) : null}
        <View style={styles.legendItem}>
          <Text {...hiddenFromScreenReader()} style={styles.count}>
            2
          </Text>
          <Text style={styles.legendText}>{t("calendar.legendCount")}</Text>
        </View>
        <View style={styles.legendItem}>
          <Text {...hiddenFromScreenReader()} style={[styles.num, styles.today]}>
            {Number(todayYmd.slice(8))}
          </Text>
          <Text style={styles.legendText}>{t("calendar.legendToday")}</Text>
        </View>
        <View style={styles.legendItem}>
          <View {...hiddenFromScreenReader()} style={styles.legendBox} />
          <Text style={styles.legendText}>{t("calendar.legendPicked")}</Text>
        </View>
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme, sizes: MarkSizes) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s1 },
    head: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
    month: { flex: 1, textAlign: "center", color: p.ink, fontSize: ty.sizes.base, fontWeight: "600" },
    week: { flexDirection: "row" },
    // One step more than the wrap gap, between the header and the weekdays.
    dowRow: { marginTop: s.s1 },
    dow: { flex: 1, textAlign: "center", color: p.ink3, fontSize: ty.sizes.sm, fontWeight: "700" },
    // The hit area is the whole cell. Min heights: at a large font the number
    // and the count grow the cell instead of being clipped.
    cell: { flex: 1, minHeight: CELL_STEP, alignItems: "center", justifyContent: "center" },
    box: {
      width: "100%",
      maxWidth: DAY_BOX_WIDTH,
      minHeight: DAY_BOX_HEIGHT,
      borderRadius: r.r3,
      borderWidth: 2,
      borderColor: "transparent",
      alignItems: "center",
      justifyContent: "center",
    },
    boxPicked: { borderColor: p.brand500, backgroundColor: p.brand100 },
    boxPressed: { backgroundColor: p.surface2 },
    num: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    today: { fontWeight: "700", textDecorationLine: "underline" },
    // A past day: ink2 (≥4.5:1 on both themes) and a lighter weight; it is
    // also announced as disabled. Its planned dot is untouched.
    past: { color: p.ink2, fontWeight: "400" },
    marks: { flexDirection: "row", alignItems: "center", gap: 2, minHeight: sizes.height },
    dot: { width: sizes.dot, height: sizes.dot, borderRadius: sizes.dot / 2, backgroundColor: p.ink2 },
    legend: { flexDirection: "row", flexWrap: "wrap", columnGap: s.s4, rowGap: s.s2, paddingTop: s.s3 },
    legendItem: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    legendText: { color: p.ink2, fontSize: ty.sizes.sm },
    legendBox: {
      width: 14,
      height: 14,
      borderRadius: r.r2,
      borderWidth: 2,
      borderColor: p.brand500,
      backgroundColor: p.brand100,
    },
    count: { color: p.ink2, fontSize: ty.sizes.sm, fontFamily: ty.fonts.mono },
  });
}
